import { pushPendingActionEntries } from './ActionEntrySync.js';
import { pushPendingActionTypes } from './ActionTypeSync.js';
import { pushPendingGraphs } from './GraphSync.js';
import { pushPendingPassages } from './PassageSync.js';
import { pushPendingPlanData } from './PlanSync.js';
import { pushPendingPrayers } from './PrayerSync.js';
import { pushPendingSettings } from './SettingSync.js';

const logFlushError = (context: string, error: unknown) => {
  // eslint-disable-next-line no-console
  console.error(`unload flush: failed to push pending ${context}`, error);
};

let flushingSettings = false;

/**
 * A local setting edit is normally pushed via a 1s-debounced trigger (see
 * `GlobalSyncMiddleware.ts`) or the 20s poll - `update()` doesn't await
 * either, so neither is guaranteed to have run yet if the tab closes (or
 * the user switches away) right after an edit. Pushes any still-pending
 * settings immediately, with `keepalive: true` so the request survives the
 * page going away instead of being aborted mid-flight.
 */
export const flushPendingSettingsOnHide = (): void => {
  if (flushingSettings) return;

  flushingSettings = true;
  void pushPendingSettings({ keepalive: true })
    .catch((error) => logFlushError('settings', error))
    .finally(() => {
      flushingSettings = false;
    });
};

let flushingPassages = false;

/**
 * Same rationale as `flushPendingSettingsOnHide`, for passages - a local
 * bookmark/tag edit is normally pushed via a 1s-debounced trigger or the
 * 20s poll, neither of which is guaranteed to have run yet if the tab
 * closes right after an edit.
 */
export const flushPendingPassagesOnHide = (): void => {
  if (flushingPassages) return;

  flushingPassages = true;
  void pushPendingPassages({ keepalive: true })
    .catch((error) => logFlushError('passages', error))
    .finally(() => {
      flushingPassages = false;
    });
};

let flushingPrayers = false;

/**
 * Same rationale as `flushPendingSettingsOnHide`, for prayers and prayer
 * list items.
 */
export const flushPendingPrayersOnHide = (): void => {
  if (flushingPrayers) return;

  flushingPrayers = true;
  void pushPendingPrayers({ keepalive: true })
    .catch((error) => logFlushError('prayers', error))
    .finally(() => {
      flushingPrayers = false;
    });
};

let flushingActionTypes = false;

/**
 * Same rationale as `flushPendingSettingsOnHide`, for action types (and the
 * tracking state that travels with them) - a local edit is normally pushed
 * via a 1s-debounced trigger or the 20s poll, neither of which is
 * guaranteed to have run yet if the tab closes right after an edit.
 */
export const flushPendingActionTypesOnHide = (): void => {
  if (flushingActionTypes) return;

  flushingActionTypes = true;
  void pushPendingActionTypes({ keepalive: true })
    .catch((error) => logFlushError('action types', error))
    .finally(() => {
      flushingActionTypes = false;
    });
};

let flushingActionEntries = false;

/**
 * Same rationale as `flushPendingSettingsOnHide`, for action entries.
 */
export const flushPendingActionEntriesOnHide = (): void => {
  if (flushingActionEntries) return;

  flushingActionEntries = true;
  void pushPendingActionEntries({ keepalive: true })
    .catch((error) => logFlushError('action entries', error))
    .finally(() => {
      flushingActionEntries = false;
    });
};

let flushingGraphs = false;

/**
 * Same rationale as `flushPendingSettingsOnHide`, for graphs.
 */
export const flushPendingGraphsOnHide = (): void => {
  if (flushingGraphs) return;

  flushingGraphs = true;
  void pushPendingGraphs({ keepalive: true })
    .catch((error) => logFlushError('graphs', error))
    .finally(() => {
      flushingGraphs = false;
    });
};

let flushingPlans = false;

/**
 * Same rationale as `flushPendingSettingsOnHide`, for reading plan changes
 * (days marked complete, catch-ups, unsubscribes).
 */
export const flushPendingPlansOnHide = (): void => {
  if (flushingPlans) return;

  flushingPlans = true;
  void pushPendingPlanData({ keepalive: true })
    .catch((error) => logFlushError('plans', error))
    .finally(() => {
      flushingPlans = false;
    });
};

const flushAllOnHide = (): void => {
  flushPendingSettingsOnHide();
  flushPendingPassagesOnHide();
  flushPendingPrayersOnHide();
  flushPendingActionTypesOnHide();
  flushPendingActionEntriesOnHide();
  flushPendingGraphsOnHide();
  flushPendingPlansOnHide();
};

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushAllOnHide();
  });
}

if (typeof window !== 'undefined') {
  // iOS/Safari doesn't reliably fire `visibilitychange` before a tab is
  // discarded - `pagehide` is the documented fallback for that case.
  window.addEventListener('pagehide', flushAllOnHide);
}
